import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from '@/components/ui/table';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Shield, Copy, ExternalLink, Loader2, Key, Trash2, RefreshCw } from 'lucide-react';
import { useTenantStore } from '@/hooks/useTenantStore';

interface AccessCode {
  id: string;
  created_at: string;
  expires_at: string;
  uses_left: number | null;
  is_public: boolean;
}

export default function AuditPortalManager() {
  const { tenantId } = useTenantStore();
  const [hours, setHours] = useState('48');
  const [uses, setUses] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [accessCode, setAccessCode] = useState('');
  const [portalUrl, setPortalUrl] = useState('');
  const [codes, setCodes] = useState<AccessCode[]>([]);

  async function loadCodes() {
    if (!tenantId) return;

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('audit_portal_access')
        .select('id, created_at, expires_at, uses_left, is_public')
        .eq('company_id', tenantId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading codes:', error);
        toast.error('Failed to load access codes');
      } else {
        setCodes(data || []);
      }
    } catch (e: any) {
      console.error('Error loading codes:', e);
      toast.error('Failed to load access codes');
    } finally {
      setLoading(false);
    }
  }

  async function issueCode() {
    if (!tenantId) {
      toast.error('No company selected');
      return;
    }

    setCreating(true);
    toast.info('Creating audit access code...');

    try {
      const { data, error } = await supabase.functions.invoke('audit-portal-issue', {
        body: {
          companyId: tenantId,
          hours: parseInt(hours) || 48,
          uses: uses ? parseInt(uses) : null,
          isPublic,
        },
      });

      if (error) throw error;

      const url = `${window.location.origin}/audit/${tenantId}`;
      setPortalUrl(url);

      if (!isPublic && data?.accessCode) {
        setAccessCode(data.accessCode);
        toast.success('Access code generated!');

        // Copy URL to clipboard
        if (url) {
          navigator.clipboard.writeText(`${url}\nAccess Code: ${data.accessCode}`);
        }
      } else {
        toast.success('Public portal enabled!');
      }

      // Reload the list
      await loadCodes();
    } catch (e: any) {
      console.error('Error issuing code:', e);
      toast.error(e.message || 'Failed to issue access code');
    } finally {
      setCreating(false);
    }
  }

  async function revokeCode(id: string) {
    try {
      const { error } = await supabase.functions.invoke('revoke-audit-code', {
        body: { codeId: id },
      });

      if (error) throw error;

      toast.success('Access code revoked');
      await loadCodes();
    } catch (e: any) {
      console.error('Error revoking code:', e);
      toast.error(e.message || 'Failed to revoke access code');
    }
  }

  useEffect(() => {
    if (tenantId) {
      loadCodes();
    }
  }, [tenantId]);

  function copyCode() {
    if (accessCode) {
      navigator.clipboard.writeText(accessCode);
      toast.success('Access code copied!');
    }
  }

  function copyUrl() {
    if (portalUrl) {
      navigator.clipboard.writeText(portalUrl);
      toast.success('Portal URL copied!');
    }
  }

  function openPortal() {
    if (portalUrl) {
      window.open(portalUrl, '_blank');
    }
  }

  return (
    <Card className="border-border bg-card">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <div className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-primary" />
          <CardTitle>Audit Portal Access</CardTitle>
        </div>
        <Button variant="outline" size="sm" onClick={loadCodes} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </CardHeader>
      <CardContent className="space-y-6">
        <div>
          <p className="text-sm text-muted-foreground mb-4">
            Generate access codes for external auditors or enable public access to compliance
            proofs.
          </p>

          <div className="grid gap-4">
            <div className="grid sm:grid-cols-4 gap-3 items-end">
              <div className="space-y-2">
                <Label htmlFor="hours" className="text-xs">
                  Hours
                </Label>
                <Input
                  id="hours"
                  type="number"
                  value={hours}
                  onChange={(e) => setHours(e.target.value)}
                  placeholder="48"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="uses" className="text-xs">
                  Uses
                </Label>
                <Input
                  id="uses"
                  type="number"
                  value={uses}
                  onChange={(e) => setUses(e.target.value)}
                  placeholder="∞"
                />
              </div>

              <div className="flex items-center gap-2 pt-6">
                <Switch id="public-mode" checked={isPublic} onCheckedChange={setIsPublic} />
                <Label htmlFor="public-mode" className="text-xs cursor-pointer">
                  {isPublic ? 'Public' : 'Private'}
                </Label>
              </div>

              <Button onClick={issueCode} disabled={creating}>
                {creating ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Key className="h-4 w-4 mr-2" />
                )}
                Issue Access
              </Button>
            </div>
          </div>
        </div>

        {(accessCode || portalUrl) && (
          <div className="space-y-3 pt-4 border-t">
            {portalUrl && (
              <div className="space-y-2">
                <Label className="text-xs">Portal URL</Label>
                <div className="flex gap-2">
                  <Input value={portalUrl} readOnly className="font-mono text-xs" />
                  <Button size="sm" variant="outline" onClick={copyUrl}>
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={openPortal}>
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}

            {accessCode && (
              <div className="space-y-2">
                <Label className="text-xs">Access Code</Label>
                <div className="flex gap-2">
                  <Input
                    value={accessCode}
                    readOnly
                    className="font-mono text-lg font-bold tracking-wider"
                  />
                  <Button size="sm" variant="outline" onClick={copyCode}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Share this code with auditors. Valid for {hours} hours
                  {uses && ` with ${uses} use(s)`}.
                </p>
              </div>
            )}
          </div>
        )}

        <div className="pt-4 border-t">
          <h3 className="text-sm font-semibold mb-3">Active Access Codes</h3>
          {loading ? (
            <div className="p-6 flex justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : codes.length > 0 ? (
            <div className="rounded-lg border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Issued</TableHead>
                    <TableHead>Expires</TableHead>
                    <TableHead>Uses Left</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {codes.map((code) => (
                    <TableRow key={code.id}>
                      <TableCell className="text-xs">
                        {new Date(code.created_at).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-xs">
                        {new Date(code.expires_at).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-xs">{code.uses_left ?? '∞'}</TableCell>
                      <TableCell>
                        <span
                          className={`text-xs px-2 py-1 rounded ${
                            code.is_public
                              ? 'bg-primary/10 text-primary'
                              : 'bg-secondary text-secondary-foreground'
                          }`}
                        >
                          {code.is_public ? 'Public' : 'Private'}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="destructive" size="sm" onClick={() => revokeCode(code.id)}>
                          <Trash2 className="h-4 w-4 mr-1" />
                          Revoke
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-6 border border-dashed rounded-lg">
              No active access codes
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
